"use client";

import RichTextInput from "@/components/ui/RichTextInput";
import ProductSection from "./EditProductSection";
import { ProductClientState } from "@/zod/product.schema";
import ReorderGroupWrapper from "../ReorderGroupWrapper";
import { useFormContext, useWatch } from "react-hook-form";

interface FireStanSectionProps {
  currentLang: "en" | "fa";
  isOpen: boolean;
  onToggle: () => void;
  submitAttempted?: boolean;
}

function FireStanSection({
  currentLang,
  isOpen,
  onToggle,
  submitAttempted = false,
}: FireStanSectionProps) {
  const { control, setValue, formState } = useFormContext<ProductClientState>();

  const values = useWatch({ control, name: "fire_standards" });

  const invalid = !!formState.errors.fire_standards;
  const dir = currentLang === "fa" ? "rtl" : "ltr";
  const editorAlign = currentLang === "fa" ? "text-right" : "text-left";

  return (
    <ProductSection
      title="Fire Standards"
      faTitle="استانداردهای حرارتی"
      showFa={currentLang === "fa"}
      dir={dir}
      textAlign={currentLang === "fa" ? "justify" : "left"}
      open={isOpen}
      onToggle={onToggle}
      invalid={submitAttempted && invalid}
    >
      <ReorderGroupWrapper<{
        value: {
          title_en: string;
          title_fa: string;
          text_en: string;
          text_fa: string;
        };
      }>
        initialItems={
          values?.map((i) => ({
            value: {
              title_en: i.title_en,
              title_fa: i.title_fa ?? "",
              text_en: i.text_en,
              text_fa: i.text_fa ?? "",
            },
          })) ?? []
        }
        newItemTemplate={{
          value: { title_en: "", title_fa: "", text_en: "", text_fa: "" },
        }}
        confirmDeleteMessage={
          currentLang === "fa"
            ? "با حذف این مورد، عنوان و متنِ انگلیسی و فارسی آن هر دو حذف می‌شوند. ادامه می‌دهید؟"
            : "Deleting this item will remove both EN and FA title and text. Continue?"
        }
        onChange={(vals) =>
          setValue(
            "fire_standards",
            vals.map((val) => val.value),
          )
        }
        renderItem={(item, index, update) => (
          <div className="space-y-2">
            <RichTextInput
              placeholder={currentLang === "fa" ? "عنوان" : "Title"}
              // ✅ soft-migration
              value={item.value[`title_${currentLang}`] ?? ""}
              // ✅ JSON-only save
              onChange={({ json }) =>
                update({
                  value: {
                    ...item.value,
                    [`title_${currentLang}`]: JSON.stringify(json),
                  },
                })
              }
              className="rounded-md border border-gray-200 bg-white"
              editorClassName={editorAlign}
              dir={dir as "ltr" | "rtl"}
              minHeightClassName="min-h-[3rem]"
            />

            {submitAttempted && (
              <p className="-mt-2 text-xs text-red-500">
                {(formState.errors as any)?.fire_standards?.[index]?.title_en
                  ?.message ||
                  (formState.errors as any)?.fire_standards?.[index]?.title_fa
                    ?.message ||
                  ""}
              </p>
            )}

            <RichTextInput
              placeholder={currentLang === "fa" ? "متن" : "Text"}
              // ✅ soft-migration
              value={item.value[`text_${currentLang}`] ?? ""}
              // ✅ JSON-only save
              onChange={({ json }) =>
                update({
                  value: {
                    ...item.value,
                    [`text_${currentLang}`]: JSON.stringify(json),
                  },
                })
              }
              className="rounded-md border border-gray-200 bg-white"
              editorClassName={editorAlign}
              dir={dir as "ltr" | "rtl"}
              minHeightClassName="min-h-[4rem]"
            />

            {submitAttempted && (
              <p className="-mt-2 text-xs text-red-500">
                {(formState.errors as any)?.fire_standards?.[index]?.text_en
                  ?.message ||
                  (formState.errors as any)?.fire_standards?.[index]?.text_fa
                    ?.message ||
                  ""}
              </p>
            )}
          </div>
        )}
      />
    </ProductSection>
  );
}

export default FireStanSection;
